import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getCourseDetailsByIdForNotification } from '../../../services/userService';
import { encodeData } from '../../../utils/encodeAndEncode';
import moment from 'moment-timezone';

function NotificationDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { notification } = location.state || {};
  const [courseDetails, setCourseDetails] = useState(null);

  console.log('NotificationDetails - location.state:', location.state);
  console.log('NotificationDetails - notification:', notification);

  useEffect(() => {
    const fetchCourseDetails = async () => {
      if (notification?.course_id) {
        try {
          const response = await getCourseDetailsByIdForNotification({
            course_id: notification.course_id
          });
          console.log('Course Details:', response.data);
          setCourseDetails(response.data);
        } catch (error) {
          console.error('Error fetching course details:', error);
        }
      }
    };

    fetchCourseDetails();
  }, [notification]);

  if (!notification) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 text-amber-600 mb-4">
            <Icon icon="mdi:alert-circle-outline" width="32" height="32" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Notification Not Found</h2>
          <p className="text-gray-600 mb-6">
            The notification you're looking for could not be found. Please access notifications from the notifications page.
          </p>
          <button
            onClick={() => navigate('/user/notifications')}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-colors"
          >
            <Icon icon="mdi:arrow-left" width="20" height="20" />
            Go to Notifications
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleStartLearning = () => {
    if (!courseDetails) return;

    const encodedCourseId = encodeData({ id: notification.course_id });
    
    // If course is already purchased/enrolled, go directly to watch page
    if (courseDetails.is_course_purchase) {
      navigate(`/user/courses/WatchCourse/${encodeURIComponent(encodedCourseId)}`);
    } else {
      // For non-enrolled courses, redirect to course details page
      navigate(`/user/courses/courseDetails/${encodeURIComponent(encodedCourseId)}`);
    }
  };

  

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="flex flex-col gap-6">
        {/* Header with back button */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            aria-label="Go back"
          >
            <Icon icon="mdi:arrow-left" width="20" height="20" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Notification Details</h1>
            <p className="text-sm text-gray-500 mt-0.5">{moment.utc(notification.createdAt).local().format('MMM DD, YYYY [at] hh:mm A')}</p>
          </div>
        </div>

        {/* Main notification card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Title section with icon */}
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-5 border-b border-gray-100">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon icon="mdi:bell" width="24" height="24" className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">{notification.title}</h2>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Icon icon="mdi:clock-outline" width="16" height="16" />
                  <span>{moment.utc(notification.createdAt).local().fromNow()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Message content */}
          <div className="px-6 py-5">
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{notification.body}</p>
            </div>

            {notification.file_url && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <a 
                  href={notification.file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Icon icon="mdi:paperclip" width="18" height="18" />
                  <span className="font-medium">View Attachment</span>
                  <Icon icon="mdi:open-in-new" width="16" height="16" className="ml-1" />
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Course card if available */}
        {notification.course_id && courseDetails && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Related Course</h3>
            </div>
            <div className="flex flex-col md:flex-row">
              <div className="w-full md:w-2/5 h-64 md:h-auto">
                <img
                  src={courseDetails.imageName}
                  alt={courseDetails.course_name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=Course+Image';
                  }}
                />
              </div>
              <div className="w-full md:w-3/5 p-6">
                <div className="flex flex-col gap-4">
                  <h2 className="text-2xl font-semibold text-gray-900">{courseDetails.course_name}</h2>
                  <p className="text-gray-600 line-clamp-3">{courseDetails.description}</p>
                  
                  <div className="flex items-center gap-2 text-gray-600">
                    <Icon icon="mdi:account" width="20" height="20" />
                    <span className="font-medium">{courseDetails.trainer_name}</span>
                  </div>
                  
                  <div className="mt-2">
                    <button 
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium transition-colors shadow-sm"
                      onClick={handleStartLearning}
                    >
                      <Icon icon="mdi:play-circle" width="20" height="20" />
                      Start Learning
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationDetails;